import { DashboardLayout } from '../components/layout/DashboardLayout';
import { useAuth } from '../utils/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useNavigate } from 'react-router-dom';
import { 
  Calendar, 
  Video, 
  Heart, 
  Target, 
  MessageCircle, 
  CheckCircle2, 
  Clock, 
  ArrowRight,
  Sparkles,
  Lock
} from 'lucide-react';

const BOOKING_URL = "https://calendly.com/mo-claritypath/1-on-1-coaching-session-with-mo";

export default function CoachingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const hasCoachingAccess = user?.subscription_tier === 'personal_path';

  const benefits = [
    {
      icon: Heart,
      title: "Personalized Attention",
      description: "Get guidance tailored specifically to your situation, goals, and challenges."
    },
    {
      icon: Target,
      title: "Accountability Partner",
      description: "Stay on track with someone who knows your journey and checks in on your progress."
    },
    {
      icon: MessageCircle,
      title: "Deeper Conversations",
      description: "Explore topics that need more nuance than text-based coaching can provide."
    },
    {
      icon: Sparkles,
      title: "Breakthrough Moments",
      description: "Experience insights and clarity that come from real-time, dynamic conversation."
    }
  ];

  const whatToExpect = [
    "A safe, judgment-free space to explore what's on your mind",
    "Practical strategies you can implement right away",
    "Follow-up notes and action items after each session",
    "Flexibility to focus on whatever matters most to you"
  ];

  return (
    <DashboardLayout>
      <div className="space-y-8 max-w-4xl mx-auto" data-testid="coaching-page">
        {/* Hero Section */}
        <div className="text-center space-y-4">
          <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium">
            <Video size={16} />
            Personal Path Exclusive
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-serif">
            1-on-1 Coaching with Mo
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Sometimes you need more than an AI coach. Book a personal session to go deeper, 
            get unstuck, and create real momentum in your life.
          </p>
        </div>

        {/* Access Card - Shows booking or upgrade prompt */}
        <Card className="p-8 rounded-2xl border-border bg-gradient-to-br from-primary/5 to-accent/5">
          {hasCoachingAccess ? (
            <div className="text-center space-y-6">
              <div className="space-y-2">
                <h2 className="text-2xl font-serif">Ready to Connect?</h2>
                <p className="text-muted-foreground">
                  Schedule your next coaching session at a time that works for you.
                </p>
              </div>
              
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Button 
                  size="lg"
                  className="rounded-full px-8 gap-2"
                  onClick={() => window.open(BOOKING_URL, '_blank')}
                  data-testid="book-session-btn"
                >
                  <Calendar size={18} />
                  Book Your Session
                </Button>
              </div>

              <div className="flex items-center justify-center gap-6 text-sm text-muted-foreground">
                <span className="flex items-center gap-1">
                  <Clock size={14} />
                  60-minute sessions
                </span>
                <span className="flex items-center gap-1">
                  <Video size={14} />
                  Video call
                </span>
              </div>
            </div>
          ) : (
            <div className="text-center space-y-6">
              <div className="w-16 h-16 mx-auto rounded-full bg-muted/50 flex items-center justify-center">
                <Lock className="text-muted-foreground" size={28} />
              </div>
              <div className="space-y-2">
                <h2 className="text-2xl font-serif">Unlock 1-on-1 Coaching</h2>
                <p className="text-muted-foreground max-w-md mx-auto">
                  Personal coaching sessions are available exclusively for Personal Path members. 
                  Upgrade to get direct access to Mo for personalized guidance.
                </p>
              </div>
              
              <Button 
                size="lg"
                className="rounded-full px-8 gap-2"
                onClick={() => navigate('/pricing')}
                data-testid="upgrade-coaching-btn"
              >
                View Personal Path
                <ArrowRight size={16} />
              </Button>
              
              <p className="text-sm text-muted-foreground">
                Personal Path is $99/month and includes unlimited AI coaching, 
                all content, and 1-on-1 sessions.
              </p>
            </div>
          )}
        </Card>

        {/* Benefits Grid */}
        <div className="space-y-6">
          <h2 className="text-2xl font-serif text-center">Why 1-on-1 Coaching?</h2>
          <div className="grid md:grid-cols-2 gap-4">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <Card 
                  key={benefit.title} 
                  className="p-6 rounded-2xl border-border hover:border-primary/30 transition-colors"
                  data-testid={`benefit-${index}`}
                >
                  <div className="flex gap-4">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Icon className="text-primary" size={20} />
                    </div>
                    <div>
                      <h3 className="font-medium mb-1">{benefit.title}</h3>
                      <p className="text-sm text-muted-foreground">{benefit.description}</p>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        </div>

        {/* What to Expect */}
        <Card className="p-8 rounded-2xl border-border">
          <h2 className="text-2xl font-serif mb-6">What to Expect</h2>
          <div className="space-y-4">
            {whatToExpect.map((item, index) => (
              <div key={`expect-${item.slice(0,15)}`} className="flex items-start gap-3" data-testid={`expect-${index}`}>
                <CheckCircle2 className="text-accent flex-shrink-0 mt-0.5" size={20} />
                <span className="text-foreground">{item}</span>
              </div>
            ))}
          </div>
        </Card>

        {/* About Mo Section */}
        <Card className="p-8 rounded-2xl border-border">
          <div className="flex flex-col md:flex-row gap-6 items-center md:items-start">
            <img 
              src="https://customer-assets.emergentagent.com/job_61a20faa-b039-4c91-a239-c76764242430/artifacts/a615gaxg_ChatGPT%20Image%20Mar%204%2C%202026%2C%2003_21_37%20PM.png"
              alt="Mo Stemen"
              className="w-24 h-24 rounded-full object-cover flex-shrink-0 shadow-md"
              data-testid="coaching-mo-photo"
            />
            <div className="text-center md:text-left space-y-3">
              <h2 className="text-2xl font-serif">Meet Mo</h2>
              <p className="text-muted-foreground">
                Mo is the founder of ClarityPath and a certified life coach dedicated to helping 
                people find direction and build lives they're proud of. With years of experience 
                guiding individuals through career transitions, relationship challenges, and 
                personal growth, Mo brings warmth, honesty, and practical wisdom to every session.
              </p>
              <p className="text-muted-foreground">
                Whether you're navigating a major decision, feeling stuck, or simply want someone 
                in your corner as you work toward your goals — Mo is here to help.
              </p>
            </div>
          </div>
        </Card>

        {/* Bottom CTA for users with access */}
        {hasCoachingAccess && (
          <div className="text-center pb-8">
            <Button 
              size="lg"
              className="rounded-full px-8 gap-2"
              onClick={() => window.open(BOOKING_URL, '_blank')}
              data-testid="book-session-btn-bottom"
            >
              <Calendar size={18} />
              Book Your Session
            </Button>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
